import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    FormGroup,
    Label,
    Input,
    Container,
    FormFeedback,
    Form,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useNavigate, useParams, Link } from "react-router-dom";
import ResultHandler from "../../../components/ResultHandler";
import FormButtonHandler from "../../../components/FormButtonHandler";
import SearchSelectBox from "../../../components/SearchSelectBox";
import { useResponse } from "../../../hooks/useResponse";
import { useTranslation } from "react-i18next";
import useCustomValidation from "../validationSchema";
import { getLead, editLead } from "../../../api/lead";
import { getLeadSources } from "../../../api/leadSource";
import { getAdmins } from "../../../api/admins";
import { getBusinessCategories } from "../../../api/businessCategory";

const EditLead = () => {
    const { t } = useTranslation();
    const { editLeadSchema } = useCustomValidation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [leadData, setLeadData] = useState(null);

    const { setData, loading, error, success } = useResponse(editLead, () => {
        navigate("/leads");
    });

    useEffect(() => {
        const fetchLead = async () => {
            try {
                const res = await getLead(id);
                const data = res?.data;
                console.log(data);
                setLeadData({
                    name: data.name || "",
                    email: data.email || "",
                    phone: data.phone || "",
                    whatsApp: data.whatsApp || "",
                    leadSource: data.leadSource
                        ? { value: data.leadSource.id, label: data.leadSource.name }
                        : null,
                    assignedTo: data.assignedTo
                        ? { value: data.assignedTo.id, label: data.assignedTo.name }
                        : null,
                    businessCategory: data.businessCategory
                        ? { value: data.businessCategory._id, label: data.businessCategory.name }
                        : null,
                });
            } catch (err) {
                console.error("Failed to fetch lead data", err);
            }
        };

        fetchLead();
    }, [id]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: leadData || {
            name: "",
            email: "",
            phone: "",
            whatsApp: "",
            leadSource: null,
            assignedTo: null,
            businessCategory: null,
        },
        validationSchema: editLeadSchema,
        onSubmit: (values) => {
            const updatedData = {
                name: values.name,
                email: values.email,
                phone: values.phone,
                whatsApp: values.whatsApp,
                leadSource: values.leadSource?.value,
                assignedTo: values.assignedTo?.value,
                businessCategory: values.businessCategory?.value,
            };
            setData({ id, data: updatedData, type: "edit" });
        },

    });

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs title={`${t("btns.Edit")} ${t("Lead")}`} />

                <Form
                    className="needs-validation"
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                    }}
                >
                    <Row>
                        {/* Reuse all fields as in AddLead */}
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Name")}</Label>
                                <Input
                                    name="name"
                                    placeholder={t("Name")}
                                    type="text"
                                    onChange={validation.handleChange}
                                    value={validation.values.name}
                                    invalid={!!(validation.touched.name && validation.errors.name)}
                                />
                                <FormFeedback>{validation.errors.name}</FormFeedback>
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Email")}</Label>
                                <Input
                                    name="email"
                                    placeholder={t("Email")}
                                    type="email"
                                    onChange={validation.handleChange}
                                    value={validation.values.email}
                                    invalid={!!(validation.touched.email && validation.errors.email)}
                                />
                                <FormFeedback>{validation.errors.email}</FormFeedback>
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Phone")}</Label>
                                <Input
                                    name="phone"
                                    placeholder={t("Phone")}
                                    type="text"
                                    onChange={validation.handleChange}
                                    value={validation.values.phone}
                                    invalid={!!(validation.touched.phone && validation.errors.phone)}
                                />
                                <FormFeedback>{validation.errors.phone}</FormFeedback>
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("WhatsApp")}</Label>
                                <Input
                                    name="whatsApp"
                                    placeholder={t("WhatsApp")}
                                    type="text"
                                    onChange={validation.handleChange}
                                    value={validation.values.whatsApp}
                                    invalid={!!(validation.touched.whatsApp && validation.errors.whatsApp)}
                                />
                                <FormFeedback>{validation.errors.whatsApp}</FormFeedback>
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Lead Source")}</Label>
                                <SearchSelectBox
                                    api={getLeadSources}
                                    name="leadSource"
                                    placeholder={t("Select Lead Source")}
                                    value={validation.values.leadSource}
                                    label="name"
                                    setValue={(value) => validation.setFieldValue("leadSource", value)}
                                    error={!!(validation.touched.leadSource && validation.errors.leadSource)}
                                />
                                {validation.touched.leadSource && validation.errors.leadSource && (
                                    <div className="text-danger mt-1">{validation.errors.leadSource}</div>
                                )}
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Assigned To")}</Label>
                                <SearchSelectBox
                                    api={getAdmins}
                                    name="assignedTo"
                                    placeholder={t("Select User")}
                                    value={validation.values.assignedTo}
                                    label="name"
                                    setValue={(value) => validation.setFieldValue("assignedTo", value)}
                                    error={!!(validation.touched.assignedTo && validation.errors.assignedTo)}
                                />
                                {validation.touched.assignedTo && validation.errors.assignedTo && (
                                    <div className="text-danger mt-1">{validation.errors.assignedTo}</div>
                                )}
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Business Category")}</Label>
                                <SearchSelectBox
                                    api={getBusinessCategories}
                                    name="businessCategory"
                                    placeholder={t("Select Business Category")}
                                    value={validation.values.businessCategory}
                                    label="name"
                                    setValue={(value) => validation.setFieldValue("businessCategory", value)}
                                    error={!!(validation.touched.businessCategory && validation.errors.businessCategory)}
                                />
                                {validation.touched.businessCategory && validation.errors.businessCategory && (
                                    <div className="text-danger mt-1">{validation.errors.businessCategory}</div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormButtonHandler btnTxt={t("Update")} loading={loading} />

                    <ResultHandler
                        error={error}
                        success={success}
                        successMsg={t("Updated Successfully!")}
                    />
                </Form>
            </Container>
        </div>
    );
};

export default EditLead;
